import {CreditNote, IApplyCreditNoteDTO} from 'app/blocks/model/credit-note.model';
import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse, HttpParams, HttpHeaders} from '@angular/common/http';
import {Resource} from 'app/constants/resource';
import {ICreditNote} from 'app/blocks/model/credit-note.model';
import EntityCrudService from 'app/blocks/service/api/entity-service';
import {SERVER_API_URL} from 'app/app.constants';
import {IExportSetting} from 'app/blocks/model/export-settings.model';
import {IApplyPaymentDTO} from 'app/blocks/model/payment.model';
import {lastValueFrom} from 'rxjs';
import {createRequestOption} from 'app/blocks/util/request-util';

@Injectable({
    providedIn: 'root'
})
export class CreditNoteService extends EntityCrudService<ICreditNote> {
    constructor(http: HttpClient) {
        super(http, Resource.CREDIT_NOTES, (obj?) => new CreditNote(obj));
    }

    generatePdf = (id: number): Promise<HttpResponse<any>> => {
        return this.http.post<any>(SERVER_API_URL + 'api/credit-notes/' + id + '/generate-pdf', null, {observe: 'response'}).toPromise();
    };

    public approveCreditNote = (creditNoteId): any => {
        return this.http
            .post<any>(SERVER_API_URL + 'api/credit-notes/' + creditNoteId + '/approve' + status, null, {observe: 'response'})
            .toPromise();
    };

    public approveCreditNoteWithInventoryAdjustment = (creditNoteId, approveDate, approveTime): any => {
        const requestParams = new HttpParams().set('approveDate', approveDate).set('approveTime', approveTime);

        return this.http
            .post<any>(SERVER_API_URL + 'api/credit-notes/' + creditNoteId + '/approve-with-inv-adjustment' + status, null, {
                observe: 'response',
                params: requestParams
            })
            .toPromise();
    };

    public unAllocateInvoiceFromCreditNote = (creditNoteId: number, invoiceId: any): any => {
        const requestParams = new HttpParams().set('invoiceId', invoiceId);
        return this.http
            .post<any>(SERVER_API_URL + 'api/credit-notes/' + creditNoteId + '/remove-invoice', null, {observe: 'response', params: requestParams})
            .toPromise();
    };

    public bulkApproveCreditNote = async (creditNoteIds): Promise<any> => {
        return this.http.post<any>(SERVER_API_URL + 'api/credit-notes/bulk-approve' + status, creditNoteIds, {observe: 'response'}).toPromise();
    };

    public bulkApproveCreditNoteWithInventoryAdjustment = async (creditNoteIds, approvalDate, approvalTime): Promise<any> => {
        const body = {
            creditNoteIds: creditNoteIds,
            approvalDate: approvalDate,
            approvalTime: approvalTime
        };

        return this.http
            .post<any>(SERVER_API_URL + 'api/credit-notes/bulk-approve-with-inv-adjustment' + status, body, {observe: 'response'})
            .toPromise();
    };

    public cancelCreditNote = (creditNoteId): any => {
        return this.http.post<any>(SERVER_API_URL + 'api/credit-notes/' + creditNoteId + '/cancel' + status, null, {observe: 'response'}).toPromise();
    };

    applyCreditNotesToInvoices = (creditNoteId: string, invoices: IApplyCreditNoteDTO[]): any => {
        return this.http.post<any>(`${SERVER_API_URL}api/credit-notes/${creditNoteId}/allocate`, invoices, {observe: 'response'}).toPromise();
    };

    public downloadPDF = (creditNoteId: number): any => {
        const REQUEST_URI = `${SERVER_API_URL}api/credit-notes/${creditNoteId}/pdf`;
        return this.http.get(REQUEST_URI, {responseType: 'arraybuffer'});
    };

    public downloadMergedCreditNotes = (creditNoteIds: number[]): Promise<any> => {
        const requestParams = new HttpParams().set('ids', creditNoteIds.toString());
        const REQUEST_URI = `${SERVER_API_URL}api/credit-notes/download-v2`;
        return this.http
            .get(REQUEST_URI, {
                responseType: 'arraybuffer',
                params: requestParams
            })
            .toPromise();
    };

    public saveCreditNote(creditNote: any): any {
        return this.http
            .post<any>(SERVER_API_URL + 'api/credit-notes', creditNote, {
                observe: 'response'
            })
            .toPromise();
    }

    toggleCreditNoteExportedStatus = (ids: number[]): any => {
        const requestParams = new HttpParams().set('ids', ids.join(','));
        const responseObservable$ = this.http.post<any>(SERVER_API_URL + 'api/credit-notes/toggle-export-status', null, {
            observe: 'response',
            params: requestParams
        });
        return lastValueFrom(responseObservable$);
    };

    getUnAppliedCreditNotesForCustomer = (customerId: number, sortReq: any): any => {
        const requestParams = createRequestOption(sortReq);
        return this.http
            .get(SERVER_API_URL + 'api/customers/' + customerId + '/unapplied-credit-notes', {observe: 'response', params: requestParams})
            .toPromise();
    };

    getUnAppliedCreditNotesForCustomerGroup = (customerGroupId: number, sortReq: any): any => {
        const requestParams = createRequestOption(sortReq);
        return this.http
            .get(SERVER_API_URL + 'api/customers-groups/' + customerGroupId + '/unapplied-credit-notes', {observe: 'response', params: requestParams})
            .toPromise();
    };
}
